/**
 * TrimHandles Component
 * 
 * Displays draggable trim handles on the timeline for setting
 * video in/out points. Also shows the trimmed region overlay.
 */

import { useCallback, useState, useEffect } from 'react';
import { useProjectStore } from '@/store/projectStore';
import clsx from 'clsx';
import { Scissors } from 'lucide-react';

interface TrimHandlesProps {
  duration: number;
  pixelsPerSecond: number;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const TrimHandles = ({ duration, pixelsPerSecond, containerRef }: TrimHandlesProps) => {
  const trimRange = useProjectStore((state) => state.project.trimRange);
  const setTrimStart = useProjectStore((state) => state.setTrimStart);
  const setTrimEnd = useProjectStore((state) => state.setTrimEnd);
  
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [initialTime, setInitialTime] = useState(0);
  
  const startTime = trimRange?.startTime ?? 0;
  const endTime = trimRange?.endTime ?? duration;
  const isEnabled = trimRange?.enabled ?? false;
  
  const startX = startTime * pixelsPerSecond;
  const endX = endTime * pixelsPerSecond;
  const trimWidth = endX - startX;
  
  // Handle drag start
  const handleMouseDown = useCallback((e: React.MouseEvent, type: 'start' | 'end') => {
    e.stopPropagation();
    e.preventDefault();
    
    setIsDragging(type);
    setDragStartX(e.clientX);
    setInitialTime(type === 'start' ? startTime : endTime);
  }, [startTime, endTime]);
  
  // Handle drag move and end
  useEffect(() => {
    if (!isDragging || !containerRef.current) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartX;
      const dt = dx / pixelsPerSecond;
      const newTime = initialTime + dt;
      
      if (isDragging === 'start') {
        setTrimStart(Math.max(0, Math.min(newTime, endTime - 0.5)));
      } else {
        setTrimEnd(Math.max(startTime + 0.5, Math.min(newTime, duration)));
      }
    };
    
    const handleMouseUp = () => {
      setIsDragging(null);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartX, initialTime, pixelsPerSecond, startTime, endTime, duration, setTrimStart, setTrimEnd, containerRef]);
  
  // Calculate trimmed duration
  const trimmedDuration = endTime - startTime;
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };
  
  return (
    <>
      {/* Dimmed regions outside trim range */}
      {isEnabled && (
        <>
          {/* Left dimmed region */}
          {startTime > 0 && (
            <div
              className="absolute top-0 bottom-0 bg-black/60 pointer-events-none z-5"
              style={{
                left: 0,
                width: startX,
              }}
            />
          )}
          
          {/* Right dimmed region */}
          {endTime < duration && (
            <div
              className="absolute top-0 bottom-0 bg-black/60 pointer-events-none z-5"
              style={{
                left: endX,
                right: 0,
              }}
            />
          )}
        </>
      )}
      
      {/* Trim region highlight */}
      <div
        className={clsx(
          'absolute top-0 bottom-0 border-t-2 border-b-2 pointer-events-none z-10 transition-colors',
          isEnabled ? 'border-yellow-400' : 'border-transparent'
        )}
        style={{
          left: startX,
          width: trimWidth,
        }}
      />
      
      {/* Start handle */}
      <div
        className={clsx(
          'absolute top-0 bottom-0 w-4 cursor-ew-resize z-20 group',
          isDragging === 'start' && 'ring-2 ring-yellow-400'
        )}
        style={{ left: startX - 8 }}
        onMouseDown={(e) => handleMouseDown(e, 'start')}
      >
        <div className={clsx(
          'absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 transition-colors',
          isEnabled ? 'bg-yellow-400' : 'bg-editor-text-muted',
          'group-hover:bg-yellow-300'
        )} />
        <div className={clsx(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-8 rounded flex items-center justify-center transition-colors',
          isEnabled ? 'bg-yellow-400' : 'bg-editor-surface border border-editor-border',
          'group-hover:bg-yellow-300'
        )}>
          <div className="w-0.5 h-4 bg-black/30 mx-px" />
          <div className="w-0.5 h-4 bg-black/30 mx-px" />
        </div>
        
        {/* Time tooltip */}
        {isDragging === 'start' && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/90 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            In: {formatTime(startTime)}
          </div>
        )}
      </div>
      
      {/* End handle */}
      <div
        className={clsx(
          'absolute top-0 bottom-0 w-4 cursor-ew-resize z-20 group',
          isDragging === 'end' && 'ring-2 ring-yellow-400'
        )}
        style={{ left: endX - 8 }}
        onMouseDown={(e) => handleMouseDown(e, 'end')}
      >
        <div className={clsx(
          'absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 transition-colors',
          isEnabled ? 'bg-yellow-400' : 'bg-editor-text-muted',
          'group-hover:bg-yellow-300'
        )} />
        <div className={clsx(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-8 rounded flex items-center justify-center transition-colors',
          isEnabled ? 'bg-yellow-400' : 'bg-editor-surface border border-editor-border',
          'group-hover:bg-yellow-300'
        )}>
          <div className="w-0.5 h-4 bg-black/30 mx-px" />
          <div className="w-0.5 h-4 bg-black/30 mx-px" />
        </div>
        
        {/* Time tooltip */}
        {isDragging === 'end' && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/90 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            Out: {formatTime(endTime)}
          </div>
        )}
      </div>
      
      {/* Trim info display */}
      {isEnabled && (
        <div
          className="absolute -top-6 bg-yellow-400/20 text-yellow-400 text-[10px] px-2 py-0.5 rounded flex items-center gap-1"
          style={{ left: startX + trimWidth / 2, transform: 'translateX(-50%)' }}
        >
          <Scissors size={10} />
          {formatTime(trimmedDuration)}
        </div>
      )}
    </>
  );
};

// Trim Controls Bar Component - Shows info when trim is active
export const TrimControlsBar = () => {
  const video = useProjectStore((state) => state.project.video);
  const trimRange = useProjectStore((state) => state.project.trimRange);
  const resetTrim = useProjectStore((state) => state.resetTrim);
  
  if (!video) return null;
  
  const isEnabled = trimRange?.enabled ?? false;
  const startTime = trimRange?.startTime ?? 0;
  const endTime = trimRange?.endTime ?? video.duration;
  const trimmedDuration = endTime - startTime;
  
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };
  
  // Only show the bar when trim is enabled
  if (!isEnabled) return null;
  
  return (
    <div className="flex items-center gap-3 px-3 py-1.5 bg-orange-500/10 border-t border-orange-500/30">
      <div className="flex items-center gap-1.5 text-orange-400">
        <Scissors size={14} />
        <span className="text-xs font-medium">Trim Active</span>
      </div>
      
      <div className="text-xs text-editor-text-muted">
        In: <span className="text-orange-300 font-mono">{formatTime(startTime)}</span>
      </div>
      
      <div className="text-xs text-editor-text-muted">
        Out: <span className="text-orange-300 font-mono">{formatTime(endTime)}</span>
      </div>
      
      <div className="text-xs text-editor-text-muted">
        Duration: <span className="text-orange-300 font-medium font-mono">{formatTime(trimmedDuration)}</span>
      </div>
      
      <div className="flex-1" />
      
      <button
        onClick={resetTrim}
        className="text-xs text-orange-400 hover:text-orange-300 px-2 py-1 rounded hover:bg-orange-500/20 transition-colors"
      >
        Reset Trim
      </button>
    </div>
  );
};

